
<!-- // ============================================
// 2. ProductImageGallery.vue
// ============================================ -->
<template>
    <div class="image-gallery">
        <div class="main-image-container" 
             @touchstart="onTouchStart" 
             @touchmove="onTouchMove"
             @touchend="onTouchEnd" 
             @mousedown="onTouchStart" 
             @mousemove="onTouchMove"
             @mouseup="onTouchEnd" 
             @mouseleave="onTouchEnd">
            <transition-group name="slide" tag="div" class="image-slider"
                :style="{ transform: `translateX(-${currentImageIndex * 100}%)` }">
                <div v-for="img in productImages" :key="img" class="slide-item">
                    <img :src="img" :alt="`${product.name} - ${productImages.indexOf(img) + 1}`"
                        class="main-image" @error="handleImageError" />
                </div>
            </transition-group>

            <div class="image-controls-bottom-right">
                <button class="btn-circle" :class="{ active: isFavorite }" @click="$emit('toggle-favorite')"
                    :title="$t('action.favorite')">
                    <span :class="['mdi', isFavorite ? 'mdi-cards-heart text-danger' : 'mdi-heart-outline text-dark']"></span>
                </button>
            </div>

            <div class="image-controls-top-left">
                <button class="btn-pill" @click="$emit('show-similar')">
                    <span class="mdi mdi-magnify"></span> {{ $t('action.showSimilar') }}
                </button>
            </div>

            <div class="image-controls-bottom-left">
                <div class="image-counter">
                    {{ currentImageIndex + 1 }} / {{ productImages.length }}
                </div>
            </div>
        </div>

        <div class="thumbnails-container" v-if="productImages.length > 1">
            <button v-for="(img, index) in productImages" :key="index" class="btn-thumbnail"
                :class="{ active: currentImageIndex === index }" @click="selectImage(index)">
                <img :src="img" :alt="`Image ${index + 1}`" />
            </button>
        </div>
        <div v-if="product.has_been_sold===1" class="bg-success text-light text-center py-2 w-100">{{ $t('sentence.sold') }}</div>
    </div>
</template>

<script>
import config from "@/config.js";

export default {
    name: 'ProductImageGallery',
    props: {
        product: {
            type: Object,
            required: true
        },
        isFavorite: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentImageIndex: 0,
            touchStartX: 0,
            touchEndX: 0,
            touchStartY: 0,
            touchEndY: 0,
            isDragging: false,
            minSwipeDistance: 50,
            storageUrl: config.storageUrl,
        };
    },
    computed: {
        productImages() {
            if (!this.product.pictures || this.product.pictures.length === 0) {
                return [this.getPlaceholderImage()];
            }
            return this.product.pictures.map(pic => `${this.storageUrl}/${pic}`);
        },
    },
    methods: {
        selectImage(index) {
            this.currentImageIndex = index;
        },
        handleImageError(e) {
            e.target.src = this.getPlaceholderImage();
        },
        getPlaceholderImage() {
            return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect width="400" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
        },
        onTouchStart(e) {
            this.isDragging = true;
            const touch = e.type.includes('touch') ? e.touches[0] : e;
            this.touchStartX = touch.clientX;
            this.touchStartY = touch.clientY;
            this.touchEndX = touch.clientX;
            this.touchEndY = touch.clientY;
        },
        onTouchMove(e) {
            if (!this.isDragging) return;
            const touch = e.type.includes('touch') ? e.touches[0] : e;
            this.touchEndX = touch.clientX;
            this.touchEndY = touch.clientY;

            const deltaX = touch.clientX - this.touchStartX;
            const slider = this.$el.querySelector('.image-slider');
            if (slider) {
                slider.style.transition = 'none';
                slider.style.transform = `translateX(calc(-${this.currentImageIndex * 100}% + ${deltaX}px))`;
            }
        },
        onTouchEnd() {
            if (!this.isDragging) return;
            this.isDragging = false;

            const deltaX = this.touchEndX - this.touchStartX;
            const absDeltaX = Math.abs(deltaX);
            const absDeltaY = Math.abs(this.touchEndY - this.touchStartY);

            const slider = this.$el.querySelector('.image-slider');
            if (slider) {
                slider.style.transition = 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)';
                slider.style.transform = `translateX(-${this.currentImageIndex * 100}%)`;
            }

            if (absDeltaY > absDeltaX || absDeltaX < this.minSwipeDistance) return;

            if (deltaX > 0) this.prevImage();
            else this.nextImage();
        },
        prevImage() {
            if (this.currentImageIndex > 0) {
                this.currentImageIndex--;
            } else {
                this.currentImageIndex = this.productImages.length - 1;
            }
        },
        nextImage() {
            if (this.currentImageIndex < this.productImages.length - 1) {
                this.currentImageIndex++;
            } else {
                this.currentImageIndex = 0;
            }
        },
    }
};
</script>
<style scoped>
.image-gallery {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: static;
    margin-bottom: 15px;
    top: 20px;
}

.main-image-container {
    position: relative;
    width: 100%;
    padding-bottom: 100%;
    background: #f8f8f8;
    border-radius: 8px;
    overflow: hidden;
}

.image-slider {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    transition: transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
    will-change: transform;
}

.slide-item {
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
}

.main-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    user-select: none;
    pointer-events: none;
}

/* Vue transition classes */
.slide-move {
    transition: transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-enter-active {
    transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-enter-from {
    opacity: 0;
    transform: translateX(100%);
}

.slide-enter-to {
    opacity: 1;
    transform: translateX(0);
}

.slide-leave-active {
    transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.slide-leave-to {
    opacity: 0;
    transform: translateX(-100%);
}

/* Image Controls */
.image-controls-top-left,
.image-controls-top-right,
.image-controls-bottom-left,
.image-controls-bottom-right {
    position: absolute;
    z-index: 2;
}

.image-controls-top-left {
    top: 15px;
    left: 15px;
}

.image-controls-top-right {
    top: 15px;
    right: 15px;
    display: flex;
    gap: 10px;
}

.image-controls-bottom-left {
    bottom: 15px;
    left: 15px;
}

.image-controls-bottom-right {
    bottom: 15px;
    right: 15px;
}

.btn-circle {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    font-size: 20px;
    backdrop-filter: blur(10px);
}

.btn-circle:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-circle:active {
    transform: scale(0.95);
}

.btn-circle.active {
    background: white;
}

.btn-circle.active:hover {
    background: var(--color-nz);
}

.btn-circle span {
    line-height: 1;
}

.btn-pill {
    padding: 10px 18px;
    border-radius: 25px;
    border: none;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-3);
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    white-space: nowrap;
}

.btn-pill:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-pill:active {
    transform: translateY(0);
}

.image-counter {
    padding: 8px 16px;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.75);
    color: white;
    font-size: 13px;
    font-weight: 600;
    backdrop-filter: blur(10px);
}

/* Thumbnails */
.thumbnails-container {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding: 5px 0;
    margin-left: 10px;
}

.btn-thumbnail {
    flex-shrink: 0;
    width: 70px;
    height: 70px;
    border: 2px solid var(--light);
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    background: white;
    padding: 0;
    transition: all 0.2s;
}

.btn-thumbnail:hover {
    border-color: #2196e3;
}

.btn-thumbnail.active {
    border-color: #2196e3;
    box-shadow: 0 0 0 2px rgba(255, 107, 0, 0.2);
}

.btn-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

@media (max-width: 991px) {
    .image-gallery {
        position: static;
        margin-bottom: 20px;
    }
}

@media (max-width: 576px) {
    .thumbnails-container {
        margin-left: 25px;
    }
}
</style>
